import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'

/**
 * Hook personnalisé pour synchroniser les filtres avec l'URL
 * @param {Object} options - Options de configuration
 * @param {Function} options.setFiltersAction - Action Redux pour définir les filtres
 * @param {Function} options.fetchAction - Action Redux pour récupérer les données
 * @param {Object} options.filters - État actuel des filtres
 * @param {Array} options.allowedParams - Liste des paramètres d'URL autorisés
 */
export const useUrlFilters = ({
  setFiltersAction,
  fetchAction,
  filters,
  allowedParams,
  updateUrlParams = true,
  waitForDefaultFilters = false // Paramètre pour indiquer si le hook doit attendre l'application des filtres par défaut
}) => {
  const dispatch = useDispatch()
  const initialized = useRef(false)
  const filtersApplied = useRef(waitForDefaultFilters) // Si waitForDefaultFilters est true, on considère que les filtres ne sont pas encore appliqués
  
  // Effet pour initialiser les filtres à partir de l'URL au chargement initial
  useEffect(() => {
    if (filtersApplied.current) return
    
    // Récupérer les paramètres de l'URL
    const searchParams = new URLSearchParams(window.location.search)
    const urlFilters = {}
    
    if(updateUrlParams) {
      // Extraire les filtres autorisés de l'URL
      allowedParams.forEach(param => {
        if (searchParams.has(param)) {
          // Traitement spécial pour certains types de paramètres
          if (param === 'page') {
            urlFilters[param] = parseInt(searchParams.get(param), 10)
          } else if (param === 'archived') {
            const archivedValue = searchParams.get(param) === 'true'
            urlFilters[param] = archivedValue
          } else {
            urlFilters[param] = searchParams.get(param)
          }
        }
      })
    }
    
    // Mettre à jour les filtres dans le state Redux si des filtres sont présents dans l'URL
    if (Object.keys(urlFilters).length > 0) {
      dispatch(setFiltersAction(urlFilters))
    }
    // } else {
    //   // Si on n'attend pas l'application de filtres par défaut, faire une requête initiale
    //   if (!waitForDefaultFilters) {
    //     dispatch(fetchAction(filters))
    //   }
    // }

    filtersApplied.current = true
  }, [])
  
  // Effet pour mettre à jour l'URL et déclencher la requête lorsque les filtres changent
  useEffect(() => {
    if (filtersApplied.current && !initialized.current) {
      if (!waitForDefaultFilters) {
        dispatch(fetchAction(filters))
      }
      initialized.current = true
      return
    }
    
    // La requête ne sera exécutée qu'après initialisation complète des filtres
    if(initialized.current) {
      // Créer un objet URLSearchParams pour construire les paramètres d'URL
      const searchParams = new URLSearchParams()
      
      // Ajouter les filtres non vides à l'URL
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && allowedParams.includes(key)) {
          searchParams.set(key, value)
        }
      })
      
      if(updateUrlParams) {
        // Construire la nouvelle URL avec les paramètres
        const newUrl = `${window.location.pathname}?${searchParams.toString()}`
        
        // Mettre à jour l'URL sans recharger la page
        window.history.pushState({ path: newUrl }, '', newUrl)
      }
      
      // Requête des données filtrées
      dispatch(fetchAction(filters))
    }
  }, [filters])
}